
import thematics from "../../data/thematic";
import { getLocale, getLabel } from '../helpers/locale';
import buildings from "../../data/buildings";
import { pointOnFeature } from "@turf/point-on-feature";
import { getConfig } from "configuration/config";

export type StringMap = Record<string, string | number | boolean>;

export function objectToQueryString(params: StringMap = {}): string {
    let queryString = "";
    Object.keys(params).forEach((key) => {
        if (queryString.length !== 0) queryString += "&";
        queryString += key + "=" + params[key].toString();
    });
    return queryString;
}

/**
 * Decodes to a [latitude, longitude] coordinates array.
 *
 * @param {String} polylineString
 * @param {Number} precision
 * @returns {Array}
 *
 * @see https://github.com/mapbox/polyline/blob/master/src/polyline.js#L36
 */
export function decodePolyline(polylineString: string, precision = 5): number[][] {
    const coordinates = [];
    const factor = Math.pow(10, precision);
    let index, lat, lng, byte, shift, result, latitude_change, longitude_change;
    index = 0;
    lat = 0;
    lng = 0;

    while (index < polylineString.length) {
        byte = null;
        shift = 0;
        result = 0;

        do {
            byte = polylineString.charCodeAt(index++) - 63;
            result |= (byte & 0x1f) << shift;
            shift += 5;
        } while (byte >= 0x20);

        latitude_change = ((result & 1) ? ~(result >> 1) : (result >> 1));

        shift = result = 0;

        do {
            byte = polylineString.charCodeAt(index++) - 63;
            result |= (byte & 0x1f) << shift;
            shift += 5;
        } while (byte >= 0x20);

        longitude_change = ((result & 1) ? ~(result >> 1) : (result >> 1));

        lat += latitude_change;
        lng += longitude_change;

        coordinates.push([lat / factor, lng / factor]);
    }

    return coordinates;
}


export function debounce<T extends (...args: any[]) => ReturnType<T>>(
    callback: T,
    timeout: number
): ((...args: Parameters<T>) => void) {
    let timer: ReturnType<typeof setTimeout>

    return (...args: Parameters<T>) => {
        clearTimeout(timer)
        timer = setTimeout(() => {
            callback(...args)
        }, timeout)
    }
}


export function getTextWidth(text: string): number {
    const canvas: HTMLCanvasElement = document.getElementById("canvasMarkerWoosmap") as HTMLCanvasElement || document.createElement("canvas") as HTMLCanvasElement;
    canvas.id = "canvasMarkerWoosmap";
    const context: CanvasRenderingContext2D = canvas.getContext("2d", { willReadFrequently: true }) as CanvasRenderingContext2D;
    context.font = "bold 15px sans-serif";
    return context.measureText(text).width;
}
/**
 * Check if input is an Object
 */
export function isObject(input: unknown): input is Record<string, unknown> {
    return typeof input === 'object' && input !== null && !Array.isArray(input);
}
/**
 * Merge the source object into the target like `Object.assign` but recursively.
 * No override if key already exist in target.
 */
export function merge<T extends Record<string, any>, S extends Record<string, any>>(target: T, source: S): T & S;
export function merge<T extends any, S extends any>(target: T, source: S): T | S;
export function merge(target: unknown, source: unknown): any {
    if (!isObject(source) || !isObject(target)) {
        return target;
    }
    for (const key in source) {
        const targetProp = target[key];
        const sourceProp = source[key];

        if (isObject(sourceProp) && isObject(targetProp)) {
            merge(targetProp, sourceProp);
        } else if (!(key in target)) {
            target[key] = source[key];
        }
    }
    return target;
}

/**
 * replace all keys enclosed by curly braces (eg. {replace_str}) by given values
 */
export function replace(str: string, keyValues: Record<string, string>): string {
    let result = str;
    Object.keys(keyValues).forEach((key) => {
        result = result.replace(
            new RegExp('\\{\\s*' + key + '\\s*\\}', 'g'),
            keyValues[key]
        );
    });
    return result;
}
export function filterThematic(theme: string) {
    if (theme && (theme !== "sdm_default" || "")) {
        const jsonData = thematics["thematics"];
        const filteredTheme = Array.from(jsonData).filter((element) => {
            return element.ref.toLowerCase() === theme.toLowerCase()
        });
        if (filteredTheme[0]) {
            filteredTheme[0] = {
                ...filteredTheme[0], ...{
                    title: getLabel(getLocale().thematics,
                        theme.toLowerCase()), description: getLabel(getLocale().thematicDescription, theme.toLowerCase())
                }
            }
        }
        return filteredTheme[0];
    }
}
export function filterBuilding(pav: string) {
    if (pav && pav !== "") {
        const jsonData = buildings["buildings"];
        const filteredResult = Array.from(jsonData).filter((element) => {
            return element.ref.toLowerCase() === pav.toLowerCase()
        });
        return filteredResult[0];
    }
}
export function getFeaturePosition(feature: any): woosmap.map.LatLngLiteral {
    let position: woosmap.map.LatLngLiteral = { lat: 0, lng: 0 };
    if (feature && feature.geometry && feature.geometry.type === 'Polygon') {
        const center = pointOnFeature(feature);
        position = {
            lat: center.geometry.coordinates[1],
            lng: center.geometry.coordinates[0]
        }
    }
    else if (feature && feature.geometry && feature.geometry.type === 'Point') {
        position = { lat: feature.geometry.coordinates[1], lng: feature.geometry.coordinates[0] }
    }
    return position;
}
export function showToast(message: string, type: 'success' | 'error') {
    const container = document.getElementById("woos-toast-container");
    if (!container) return;
    const existingToast = container.querySelector(`.woos-toast.${type}`);
    if (existingToast) {
        return;
    }

    const toast = document.createElement("div");
    toast.className = `woos-toast ${type}`;
    toast.textContent = message;

    container.appendChild(toast);

    setTimeout(() => {
        toast.remove();
    }, 4500);
}
export function isMobileView(): boolean {
    const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    if (width < getConfig().map.mobileBreakPoint) {
        return true;
    }
    return false;
}
export function computePaddingForPOIPanel(isMobile=false) {
    const defaultPadding = {
        top: 0, bottom: 0, left: 0, right: 0
    };
    let panelHeight = 0;
    let panelWidth = 0;
    const headerContent = document.getElementsByClassName("woosmap-panel__content__header")[0];
    const bodyContent = document.getElementsByClassName("woosmap-panel__content__body")[0];
    const searchBarContainer = document.getElementsByClassName("woosmap-panel__header")[0];

    if (searchBarContainer && headerContent && bodyContent) {
        panelHeight = headerContent.clientHeight + bodyContent.clientHeight + searchBarContainer.clientHeight;
        panelWidth = searchBarContainer.clientWidth;
    }
    panelHeight = isMobile ? 0 : panelHeight;
    if (isMobileView()) {
        defaultPadding.bottom = panelHeight;
    }
    else {
        defaultPadding.left = panelWidth;
    }
    return defaultPadding;
}